import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Package, MapPin, Calendar, Banknote, Percent, ArrowRight, CheckCircle2, Award, Lock,
} from "lucide-react";

const corpImg =
  "https://images.pexels.com/photos/1767664/pexels-photo-1767664.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function VorratsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/vorratsgesellschaften")
      .then(({ data }) => { setItems(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const { available, sold } = useMemo(() => ({
    available: items.filter((v) => !v.sold),
    sold: items.filter((v) => v.sold),
  }), [items]);

  return (
    <div className="pt-32 pb-24" data-testid="vorrats-page">
      <section className="relative mbw-container mb-12">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="mbw-overline mb-4">Aktuelle Angebote</div>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter text-[#1A2433] leading-[1.02] mb-6">
              Firmen <span className="text-[#991B1B]">zum Verkauf.</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-2xl">
              Unsere Kunden schätzen den schnellen Start ihrer Unternehmung durch den Kauf
              einer bereits bestehenden Firma. In den meisten Fällen ist eine GmbH oder
              Aktiengesellschaft die passende Unternehmensform. Wir bieten die Übernahme
              von Vorratsgesellschaften an, die bereits im Handelsregister stehen, aber
              noch keine Geschäftstätigkeit aufgenommen haben.{" "}
              <strong>Name, Zweck und Sitz sind individuell anpassbar.</strong>
            </p>
          </div>
          <div className="lg:col-span-5">
            <img src={corpImg} alt="Schweizer Geschäftsgebäude" className="rounded-2xl shadow-xl w-full h-72 object-cover" />
          </div>
        </div>
      </section>

      {/* Social proof bar */}
      {!loading && (available.length > 0 || sold.length > 0) && (
        <div className="mbw-container mb-10">
          <div className="bg-[#1A2433] text-white rounded-2xl px-6 py-5 flex flex-wrap items-center justify-between gap-4" data-testid="vorrats-stats">
            <div className="flex items-center gap-3">
              <Award className="w-6 h-6 text-[#DC2626]" />
              <div>
                <div className="font-heading font-bold text-lg">
                  {sold.length} Gesellschaft{sold.length === 1 ? "" : "en"} bereits erfolgreich vermittelt
                </div>
                <div className="text-sm text-slate-300">
                  {available.length} aktuell verfügbar · individuell anpassbar
                </div>
              </div>
            </div>
            <Link to="/kontakt" className="text-sm font-semibold text-[#DC2626] hover:underline inline-flex items-center gap-1.5">
              Wunsch-Firma anfragen <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}

      <div className="mbw-container">
        {loading ? (
          <div className="text-center py-16 text-slate-500">Lade Angebote…</div>
        ) : (
          <>
            {/* Verfügbar */}
            {available.length > 0 && (
              <>
                <h2 className="font-heading text-2xl font-bold text-[#1A2433] mb-5" data-testid="vorrats-available-title">
                  Verfügbar <span className="text-slate-400 font-normal text-base">({available.length})</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
                  {available.map((v, i) => (
                    <VorratCard key={v.id} v={v} i={i} />
                  ))}
                </div>
              </>
            )}

            {available.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8 mb-14 text-center" data-testid="vorrats-empty">
                <h2 className="font-heading text-xl font-bold text-[#1A2433] mb-2">
                  Aktuell sind alle Gesellschaften vermittelt.
                </h2>
                <p className="text-slate-600 mb-4">
                  Gerne erstellen wir Ihnen eine massgeschneiderte Vorratsgesellschaft –
                  in der Regel innerhalb von 2–3 Wochen.
                </p>
                <Link to="/kontakt" className="btn-primary">
                  Wunschfirma anfragen <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}

            {/* Bereits vermittelt */}
            {sold.length > 0 && (
              <section data-testid="vorrats-sold-section">
                <div className="flex items-baseline justify-between flex-wrap gap-3 mb-5">
                  <h2 className="font-heading text-2xl font-bold text-[#1A2433]">
                    Bereits vermittelt
                    <span className="text-slate-400 font-normal text-base"> ({sold.length})</span>
                  </h2>
                  <p className="text-sm text-slate-500 max-w-md">
                    Diese Gesellschaften wurden bereits an Kunden übergeben. Ähnliche Strukturen sind auf Anfrage realisierbar.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sold.map((v, i) => (
                    <VorratCard key={v.id} v={v} i={i} sold />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function VorratCard({ v, i, sold }) {
  const soldOn = v.sold_at ? new Date(v.sold_at).toLocaleDateString("de-CH", { year: "numeric", month: "short" }) : null;

  return (
    <div
      className={`mbw-card p-8 flex flex-col relative overflow-hidden ${sold ? "opacity-90" : ""}`}
      data-testid={`vorrats-card-${sold ? "sold-" : ""}${i}`}
    >
      {/* SOLD ribbon */}
      {sold && (
        <div className="absolute top-0 right-0 z-10" data-testid={`vorrats-sold-badge-${i}`}>
          <div className="bg-emerald-600 text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-bl-xl shadow-md flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" /> Vermittelt
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-5">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${sold ? "bg-slate-400" : "bg-[#1A2433]"}`}>
          <Package className={`w-6 h-6 ${sold ? "text-white" : "text-[#DC2626]"}`} />
        </div>
        {!sold && v.highlight && (
          <span className="text-xs font-semibold uppercase tracking-wider text-[#991B1B] bg-[#991B1B]/10 px-3 py-1 rounded-full">
            {v.highlight}
          </span>
        )}
      </div>

      <h3 className={`font-heading text-2xl font-bold mb-1 ${sold ? "text-slate-500 line-through decoration-2 decoration-emerald-600/50" : "text-[#1A2433]"}`}>
        {v.name}
      </h3>
      <div className="text-sm text-slate-500 mb-5">Kennung: {v.kennung}</div>

      <dl className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm mb-6">
        <Cell label="Form" value={v.form} sold={sold} />
        <Cell label={<><Calendar className="w-3 h-3 inline mr-1" />Jahr</>} value={v.founded_year} sold={sold} />
        <Cell label={<><Banknote className="w-3 h-3 inline mr-1" />Kapital</>} value={`CHF ${v.capital_chf.toLocaleString("de-CH")}`} sold={sold} />
        <Cell label={<><Percent className="w-3 h-3 inline mr-1" />Liberiert</>} value={`${v.liberiert_percent}%`} sold={sold} />
        <Cell wide label={<><MapPin className="w-3 h-3 inline mr-1" />Sitz</>} value={v.sitz} sold={sold} />
      </dl>

      {!sold && v.zweck_anpassbar && (
        <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 px-3 py-2 rounded-md mb-5">
          <CheckCircle2 className="w-4 h-4" /> Zweck &amp; Name anpassbar
        </div>
      )}

      {sold && soldOn && (
        <div className="flex items-center gap-2 text-xs text-slate-600 bg-slate-100 px-3 py-2 rounded-md mb-5">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Vermittelt {soldOn}
        </div>
      )}

      <div className="mt-auto pt-5 border-t border-slate-200">
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{sold ? "ursprünglicher Preis" : "Kaufpreis"}</div>
        <div className={`font-heading text-3xl font-bold mb-4 tabular-nums ${sold ? "text-slate-400 line-through" : "text-[#1A2433]"}`}>
          CHF {v.price_chf.toLocaleString("de-CH")}
        </div>

        {sold ? (
          <Link
            to="/kontakt"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-white border-2 border-slate-200 text-slate-600 font-semibold hover:border-[#991B1B] hover:text-[#991B1B] transition"
            data-testid={`vorrats-cta-sold-${i}`}
          >
            <Lock className="w-4 h-4" /> Ähnliche Firma anfragen
          </Link>
        ) : (
          <Link to={`/kontakt?vorrat=${v.id}`} className="btn-primary w-full justify-center" data-testid={`vorrats-cta-${i}`}>
            Anfragen <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </div>
    </div>
  );
}

function Cell({ label, value, sold, wide }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <dt className="text-slate-500 text-xs uppercase tracking-wider">{label}</dt>
      <dd className={`font-semibold ${sold ? "text-slate-500" : "text-[#1A2433]"}`}>{value}</dd>
    </div>
  );
}
